import { useTranslation } from 'react-i18next';
import type { RoomMode } from '@os/shared';

/** The Paris time of `at` and whether Paris is on winter (UTC+1) or summer (UTC+2) time then. */
function parisTime(at: Date, locale: string): { time: string; summer: boolean } {
  const parts = new Intl.DateTimeFormat(locale, { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(at);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value);
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return { time: `${String(hour).padStart(2, '0')}:${minute}`, summer: (hour - at.getUTCHours() + 24) % 24 === 2 };
}

/**
 * The room's quota mode in words (P12, spec §18.4): a status in economy and edits-only, an alert while sync is
 * suspended that says when it resumes (Paris time of the next 00:00 UTC) and that nothing is lost.
 */
export function QuotaBanner({ mode, resetAt }: { mode: RoomMode; resetAt: string | null }) {
  const { t, i18n } = useTranslation();
  if (mode === 'normal') return null;
  const box = 'pointer-events-auto max-w-md rounded-lg border bg-surface p-3 font-sans text-sm text-fg shadow-lg';
  let body;
  if (mode === 'suspended') {
    const at = resetAt ? new Date(resetAt) : null;
    const paris = at && Number.isFinite(at.getTime()) ? parisTime(at, i18n.language) : null;
    body = (
      <div role="alert" className={`${box} border-accent`}>
        <p className="font-medium">
          {paris ? t('quota.suspended', { time: paris.time, season: t(paris.summer ? 'quota.summer' : 'quota.winter') }) : t('quota.suspendedSoon')}
        </p>
        <p className="mt-1 text-fg-muted">{t('quota.kept')}</p>
      </div>
    );
  } else {
    body = (
      <div role="status" className={`${box} border-border`}>
        <p>{t(mode === 'economy' ? 'quota.economy' : 'quota.edits')}</p>
      </div>
    );
  }
  return <div className="pointer-events-none fixed inset-x-4 top-16 z-30 flex justify-center">{body}</div>;
}
